import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, LogOut, Package, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { money, ORDER_STATUS_LABELS } from "@/config";

const STATUS_COLORS = {
  pendiente_pago: "bg-corn/30 text-cocoa",
  recibido: "bg-corn/40 text-cocoa",
  en_preparacion: "bg-habanero/15 text-habanero-dark",
  enviado: "bg-leaf/15 text-leaf",
  listo_recoger: "bg-leaf/15 text-leaf",
  completado: "bg-leaf text-white",
  cancelado: "bg-cocoa/10 text-clay",
};

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_COLORS[status] || "bg-surface text-cocoa"}`} data-testid={`order-status-${status}`}>
      {ORDER_STATUS_LABELS[status] || status}
    </span>
  );
}

function LoginForm() {
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const r = mode === "login"
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password);
    setLoading(false);
    if (!r.ok) setError(r.error);
    else toast.success(mode === "login" ? "¡Qué gusto verte de nuevo!" : "¡Bienvenida/o a la familia SoyDiVa!");
  };

  return (
    <div className="mx-auto max-w-md rounded-2xl border border-sand bg-white p-8" data-testid="auth-card">
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="grid w-full grid-cols-2 bg-surface">
          <TabsTrigger value="login" data-testid="tab-login">Entrar</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">Crear cuenta</TabsTrigger>
        </TabsList>
      </Tabs>
      <form onSubmit={submit} className="mt-6 space-y-4">
        {mode === "register" && (
          <div>
            <Label htmlFor="auth-name">Nombre</Label>
            <Input id="auth-name" data-testid="auth-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 border-sand" required />
          </div>
        )}
        <div>
          <Label htmlFor="auth-email">Correo electrónico</Label>
          <Input id="auth-email" type="email" data-testid="auth-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 border-sand" required />
        </div>
        <div>
          <Label htmlFor="auth-password">Contraseña</Label>
          <Input id="auth-password" type="password" data-testid="auth-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 border-sand" required minLength={6} />
        </div>
        {error && <p className="rounded-lg bg-destructive/10 p-3 text-sm font-semibold text-destructive" data-testid="auth-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="auth-submit-button" className="w-full rounded-full bg-habanero py-6 font-bold text-white hover:bg-habanero-dark">
          {loading ? "Un momento..." : mode === "login" ? "Entrar" : "Crear mi cuenta"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-clay">
        <Separator className="flex-1 bg-sand" /> o <Separator className="flex-1 bg-sand" />
      </div>
      <Button variant="outline" onClick={loginWithGoogle} data-testid="google-login-button" className="w-full rounded-full border-sand py-6 font-bold text-cocoa hover:bg-surface">
        <svg className="mr-2" width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.7-5 3.7-8.8z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z"/><path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z"/><path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 2.9c.9-2.9 3.6-5 6.8-5z"/></svg>
        Continuar con Google
      </Button>
    </div>
  );
}

function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.get(`/orders/track/${encodeURIComponent(orderNumber)}`, { params: { email } });
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="rounded-2xl border border-sand bg-white p-8" data-testid="track-order-card">
      <h2 className="flex items-center gap-2 font-script text-xl font-semibold text-cocoa">
        <Search size={18} className="text-habanero" /> Rastrea tu pedido
      </h2>
      <p className="mt-1 text-sm text-clay">¿Compraste sin cuenta? Ingresa tu número de pedido y correo.</p>
      <form onSubmit={track} className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <Input data-testid="track-order-number-input" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="DV-0001" className="border-sand" required />
        <Input data-testid="track-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tucorreo@ejemplo.com" className="border-sand" required />
        <Button type="submit" disabled={loading} data-testid="track-submit-button" className="rounded-full bg-cocoa px-6 font-bold text-white hover:bg-cocoa/90">
          {loading ? "Buscando..." : "Rastrear"}
        </Button>
      </form>
      {error && <p className="mt-4 rounded-lg bg-destructive/10 p-3 text-sm font-semibold text-destructive" data-testid="track-error">{error}</p>}
      {order && (
        <div className="mt-6 rounded-xl bg-surface p-5" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-display text-lg font-bold text-cocoa">{order.order_number}</p>
            <StatusBadge status={order.status} />
          </div>
          <p className="mt-1 text-sm text-clay">
            {order.delivery_method === "pickup" ? "Pick up en Mérida" : "Envío a domicilio"} · Total {money(order.total)}
          </p>
          <ul className="mt-3 space-y-1 text-sm text-cocoa">
            {order.items.map((i) => (
              <li key={`${i.slug}-${i.variant_id}`}>· {i.qty} × {i.name} ({i.variant_label})</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MyOrders() {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, []);

  if (orders === null) return <p className="py-10 text-center font-script font-semibold text-2xl text-clay">Revisando la cocina...</p>;
  if (orders.length === 0)
    return (
      <div className="py-10 text-center" data-testid="my-orders-empty">
        <p className="font-script font-semibold text-3xl text-clay">Aún no tienes pedidos</p>
        <Button asChild className="mt-4 rounded-full bg-habanero px-8 font-bold text-white hover:bg-habanero-dark">
          <Link to="/tienda">Hacer mi primer pedido</Link>
        </Button>
      </div>
    );

  return (
    <div className="space-y-4" data-testid="my-orders-list">
      {orders.map((o) => (
        <div key={o.order_number} className="rounded-2xl border border-sand bg-white p-6" data-testid={`my-order-${o.order_number}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <Package size={20} className="text-habanero" />
              <p className="font-display text-lg font-bold text-cocoa">{o.order_number}</p>
            </div>
            <StatusBadge status={o.status} />
          </div>
          <p className="mt-1 text-xs text-clay">
            {new Date(o.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })} ·{" "}
            {o.delivery_method === "pickup" ? "Pick up" : "Envío a domicilio"} ·{" "}
            {o.payment_status === "paid" ? "Pagado" : "Pendiente de pago"}
          </p>
          <ul className="mt-3 space-y-1 text-sm text-cocoa">
            {o.items.map((i) => (
              <li key={`${i.slug}-${i.variant_id}`}>· {i.qty} × {i.name} ({i.variant_label})</li>
            ))}
          </ul>
          <p className="mt-3 text-right font-display text-lg font-bold text-cocoa">{money(o.total)}</p>
        </div>
      ))}
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();

  if (user === null)
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Flame className="animate-pulse text-habanero" size={40} />
      </div>
    );

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-8" data-testid="account-page">
      {user ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-script font-semibold text-2xl text-orange">¡Hola de nuevo!</p>
              <h1 className="font-script text-4xl font-semibold text-cocoa" data-testid="account-name">{user.name}</h1>
              <p className="mt-1 text-sm text-clay">{user.email}</p>
            </div>
            <div className="flex gap-2">
              {user.role === "admin" && (
                <Button asChild variant="outline" className="rounded-full border-habanero text-habanero hover:bg-habanero hover:text-white" data-testid="account-admin-link">
                  <Link to="/admin">Panel admin</Link>
                </Button>
              )}
              <Button variant="outline" onClick={logout} data-testid="account-logout-button" className="rounded-full border-sand text-cocoa">
                <LogOut className="mr-2" size={16} /> Salir
              </Button>
            </div>
          </div>
          <h2 className="mt-12 font-script text-2xl font-semibold text-cocoa">Mis pedidos</h2>
          <div className="mt-6"><MyOrders /></div>
        </>
      ) : (
        <>
          <div className="text-center">
            <p className="font-script font-semibold text-2xl text-orange">Únete a la familia</p>
            <h1 className="font-script text-4xl font-semibold text-cocoa">Mi cuenta</h1>
            <p className="mx-auto mt-3 max-w-md text-clay">Entra o crea tu cuenta para ver tus pedidos y comprar más rápido.</p>
          </div>
          <div className="mt-10"><LoginForm /></div>
        </>
      )}
      <div className="mt-12"><TrackOrder /></div>
    </div>
  );
}
